import { Dialog } from "radix-ui";
import type { ReactNode } from "react";

/**
 * A centred panel over a veil, for looking at or changing one record without
 * leaving the list it came from.
 *
 * Radix for the parts that are easy to get subtly wrong — focus trapped while
 * open and returned on close, Escape, outside click, scroll lock — and the same
 * veil and spring the spotlight uses, so the two overlays read as one family.
 * The body scrolls; the header and footer stay put, so the action that closes the
 * panel is never scrolled out of reach.
 */
export function Modal({
  open,
  onOpenChange,
  eyebrow,
  title,
  description,
  children,
  footer,
  size = "default",
  status,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly eyebrow?: ReactNode;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  /**
   * `wide` for a record read as a main column plus a side column, which a
   * form-width panel squeezes into one long scroll.
   */
  readonly size?: "default" | "wide";
  /** The record's state, beside the title — a pill, usually, and a freshness caption. */
  readonly status?: ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="spotlight-veil fixed inset-0 z-40 bg-ink/20 backdrop-blur-[2px]" />
        <Dialog.Content
          {...(description === undefined ? { "aria-describedby": undefined } : {})}
          className={[
            "spotlight-panel fixed top-[8vh] left-1/2 z-50 flex max-h-[84vh] -translate-x-1/2 flex-col",
            size === "wide" ? "w-[min(68rem,calc(100vw-2rem))]" : "w-[min(44rem,calc(100vw-2rem))]",
            "overflow-hidden rounded-panel border border-rule bg-paper-raised shadow-float",
          ].join(" ")}
        >
          <header className="flex items-start justify-between gap-4 border-b border-rule px-5 pt-4 pb-3">
            <div className="flex min-w-0 flex-col gap-1">
              {eyebrow === undefined ? null : (
                <span className="text-micro font-semibold uppercase tracking-[0.11em] text-ink-faint">
                  {eyebrow}
                </span>
              )}
              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                {/* Two lines before an ellipsis: a record's title can be a sentence
                    (an order's outcome), and cutting it at one line cut the verdict. */}
                <Dialog.Title className="m-0 line-clamp-2 min-w-0 break-words text-title font-semibold text-ink">
                  {title}
                </Dialog.Title>
                {status}
              </div>
              {description === undefined ? null : (
                <Dialog.Description className="m-0 text-body text-ink-muted">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close
              aria-label="Close"
              className="-mr-1 shrink-0 px-2 py-1 text-ink-faint transition-colors duration-fast ease-swiss hover:text-ink"
            >
              <span aria-hidden="true">✕</span>
            </Dialog.Close>
          </header>

          <div className="flex min-h-0 flex-col gap-6 overflow-y-auto px-5 py-5">{children}</div>

          {footer === undefined ? null : (
            <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-rule bg-paper px-5 py-3">
              {footer}
            </footer>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
